import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../AuthContext';
import { useTheme } from '../ThemeContext';
import { Sun, Moon } from 'react-feather';
import * as Realm from "realm-web";
import { url as API_BASE_URL } from '../../MongoDb/MongoConstants';
import './MyAccount.css';
import '../shared/PageHeader.css';
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { Link } from 'react-router-dom';

const app = new Realm.App({ id: "thebrainfund-qyrogsg" });

// Helper to format numbers to two decimal places, always showing 0.00 if empty/zero
const formatNumber = (val) => {
  const num = Number(val);
  return isNaN(num) ? "0.00" : num.toFixed(2);
};

export default function MyAccount() {
  const { user, userProfile, logout } = useContext(AuthContext);
  const { isDarkMode, toggleTheme } = useTheme();
  // const [profile, setProfile] = useState(null);
  const [orders, setOrders] = useState([]);
  const [trades, setTrades] = useState([]);
  const [showOpenOrders, setShowOpenOrders] = useState(true);
  const [showBuyTrades, setShowBuyTrades] = useState(true);
  const [deletingOrder, setDeletingOrder] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('success'); // 'success', 'error'
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositError, setDepositError] = useState('');
  

useEffect(() => {
  const fetchProfile = async () => {
    if (user) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/user/profile/${user.id}`);
        const result = await response.json();
        if (result.success) {
          // Profile is already in context, but this fetches fresh data with NAV calculation
          console.log('Profile fetched:', result.data);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    }
  };
  fetchProfile();
}, [user]);

useEffect(() => {
  const fetchOrders = async () => {
    if (user) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/user/${user.id}/orders`);
        const result = await response.json();
        if (result.success) {
          setOrders(result.data);
        } else {
          setOrders([]);
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
        setOrders([]);
      }
    }
  };
  fetchOrders();
}, [user]);

useEffect(() => {
  const fetchTrades = async () => {
    if (user) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/user/${user.id}/trades`);
        const result = await response.json();
        if (result.success) {
          setTrades(result.data);
        } else {
          setTrades([]);
        }
      } catch (error) {
        console.error('Error fetching trades:', error);
        setTrades([]);
      }
    }
  };
  fetchTrades();
}, [user]);

  // Function to show message modal
  const showMessage = (message, type = 'success') => {
    setModalMessage(message);
    setModalType(type);
    setShowMessageModal(true);
  };

  // Function to handle order deletion
  const handleDeleteOrder = async (orderId) => {
    if (!user || !orderId) return;
    
    // Store order ID and show confirmation modal
    setOrderToDelete(orderId);
    setShowConfirmModal(true);
  };

  // Function to confirm order deletion
  const confirmDeleteOrder = async () => {
    if (!orderToDelete) return;
    
    setShowConfirmModal(false);
    
    // Convert ObjectId to string if necessary
    const orderIdString = typeof orderToDelete === 'object' && orderToDelete.toString ? orderToDelete.toString() : orderToDelete;
    
    setDeletingOrder(orderIdString);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/exchange/order/${orderIdString}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: user.id
        })
      });
      const result = await response.json();
      
      if (result.success) {
      setOrders(prevOrders => prevOrders.filter(order => {
        const currentOrderId = typeof order._id === 'object' && order._id.toString ? order._id.toString() : (order._id || order.id);
        return currentOrderId !== orderIdString;
          return currentOrderId !== orderIdString;
        }));
      
        showMessage('Order deleted successfully!', 'success');
      } else {
        showMessage('Error deleting order: ' + (result.error || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Error deleting order:', error);
      showMessage('Error deleting order: ' + error.message, 'error');
    } finally {
      setDeletingOrder(null);
      setOrderToDelete(null);
    }
  };

  const profile = userProfile;

  if (!user) {
    return (
      <div className="login-page-bg">
        <div className="login-card">
          <h2 className="login-title">My Account</h2>
          <p className="login-message"><Link to="/login">Please log into view your account</Link> </p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="login-page-bg">
        <div className="login-card">
          <h2 className="login-title">My Account</h2>
          <p style={{ color: '#a5a5a5' }}>Loading...</p>
        </div>
      </div>
    );
  }

  const portfolio = profile.portfolio || [];
  
  const deposit = () => {
    setShowDepositModal(true);
    setDepositAmount('');
    setDepositError('');
  };

  const handleDepositPayment = async (details) => {
    try {
      // Send deposit confirmation to backend
      const response = await fetch(`${API_BASE_URL}/api/user/${user.id}/deposit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(depositAmount),
          transaction_id: details.id,
          payment_method: 'paypal',
          payer_email: details.payer.email_address
        })
      });

      const result = await response.json();
      
      if (result.success) {
        showMessage('Deposit successful! Your account has been credited.', 'success');
        setShowDepositModal(false);
        setDepositAmount('');
        
        // Refresh profile to get updated fund_avail
        const profileResponse = await fetch(`${API_BASE_URL}/api/user/profile/${user.id}`);
        const profileResult = await profileResponse.json();
        if (profileResult.success) {
          // Profile is in context, but you may want to trigger a refresh
          console.log('Profile refreshed after deposit');
        }
      } else {
        setDepositError(result.error || 'Deposit failed. Please try again.');
      }
    } catch (error) {
      console.error('Error processing deposit:', error);
      setDepositError('Error processing deposit: ' + error.message);
    }
  };

  // Filter orders based on toggle
  const filteredOrders = orders.filter(order => {
    const status = (order.status || '').toLowerCase();
    if (showOpenOrders) {
      return status === 'open' || status === 'pending' || status === 'partial';
    } else {
      return status === 'filled' || status === 'executed' || status === 'partial';
    }
  });

  // Filter trades based on toggle
  const filteredTrades = orders.filter(order => {
    const tradeType = (order.type || '').toLowerCase();
    if (showBuyTrades) {
      return tradeType === 'buy' || tradeType === 'purchase';
    } else {
      return tradeType === 'sell' || tradeType === 'sale';
    }
  });


   return (
    <div className="login-page-bg">
      <div className="login-card">
        <h2 className="login-title">My Account</h2>
        <div className="account-layout">
          <div className="account-info">
            <div className="form-group">
              <label>Name</label>
              <input
                className="form-control"
                type="text"
                value={profile.name || ""}
                disabled
                style={{ background: '#232325', color: '#a5a5a5', border: 'none' }}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                className="form-control"
                type="email"
                value={profile.email || user.profile?.email || ""}
                disabled
                style={{ background: '#232325', color: '#a5a5a5', border: 'none' }}
              />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input
                className="form-control"
                type="text"
                value={profile.phone || user.profile?.phone || ""}
                disabled
                style={{ background: '#232325', color: '#a5a5a5', border: 'none' }}
              />
            </div>
            {/* FUND VALUES */}
            <div className="form-row">
            <div className="form-group" style={{ flex: 1, marginRight: 8 }}>
                <label>Fund Value</label>
                <input
                className="form-control"
                type="text"
                value={formatNumber(profile.fund_nav) ?? user.profile?.fund_nav ?? ""}
                disabled
                style={{ background: '#232325', color: '#a5a5a5', border: 'none' }}
                />
            </div>
            <div className="form-group" style={{ flex: 1, marginLeft: 8 }}>
                <label>Fund Available</label>
                <input
                className="form-control"
                type="text"
                value={formatNumber(profile.fund_avail) ?? user.profile?.fund_avail ?? ""}
                disabled
                style={{ background: '#232325', color: '#a5a5a5', border: 'none' }}
                />
            </div>
            </div>
            {/* EXCHANGE VALUES */}
            <div className="form-row">
            <div className="form-group" style={{ flex: 1, marginRight: 8 }}>
                <label>Exchange Value</label>
                <input
                className="form-control"
                type="text"
                value={formatNumber(profile.exchange_nav) ?? user.profile?.exchange_nav ?? ""}
                disabled
                style={{ background: '#232325', color: '#a5a5a5', border: 'none' }}
                />
            </div>
            <div className="form-group" style={{ flex: 1, marginLeft: 8 }}>
                <label>Exchange Available</label>
                <input
                className="form-control"
                type="text"
                value={formatNumber(profile.exchange_avail) ?? user.profile?.exchange_avail ?? ""}
                disabled
                style={{ background: '#232325', color: '#a5a5a5', border: 'none' }}
                />
            </div>
            </div>
            <div className="form-group">
              <label>User ID</label>
              <input
                className="form-control"
                type="text"
                value={user.id}
                disabled
                style={{ background: '#232325', color: '#a5a5a5', border: 'none' }}
              />
            </div>
            
            {/* Deposit Button */}
            <div className="form-group">
              <button
                onClick={deposit}
                style={{
                  background: isDarkMode ? '#2c8640' : '#27bd27',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '0.95em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                  width: '100%',
                }}
                onMouseOver={(e) => e.target.style.background = isDarkMode ? '#66ff7a' : '#2ecc40'}
                onMouseOut={(e) => e.target.style.background = isDarkMode ? '#44ff6d' : '#27bd27'}
              >
                Deposit
              </button>
            </div>

            {/* Theme Toggle */}
            <div className="form-group">
              <label>Theme</label>
              <div className="theme-toggle-container" style={{ marginTop: '8px' }}>
                <input
                  type="radio"
                  id="light-mode-account"
                  name="theme-account"
                  value="light"
                  checked={!isDarkMode}
                  onChange={() => !isDarkMode || toggleTheme()}
                  className="theme-radio"
                />
                <label htmlFor="light-mode-account" className="theme-label">
                  <Sun size={16} />
                  Light
                </label>
                
                <input
                  type="radio"
                  id="dark-mode-account"
                  name="theme-account"
                  value="dark"
                  checked={isDarkMode}
                  onChange={() => isDarkMode || toggleTheme()}
                  className="theme-radio"
                />
                <label htmlFor="dark-mode-account" className="theme-label">
                  <Moon size={16} />
                  Dark
                </label>
              </div>
            </div>
            
            {/* Logout Button */}
            <div className="form-group">
              <button
                onClick={logout}
                style={{
                  background: isDarkMode ? '#ff4444' : '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '0.95em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                  width: '100%',
                }}
                onMouseOver={(e) => e.target.style.background = isDarkMode ? '#ff6666' : '#c82333'}
                onMouseOut={(e) => e.target.style.background = isDarkMode ? '#ff4444' : '#dc3545'}
              >
                Logout
              </button>
            </div>
          </div>
          
          <div className="portfolio-sections">
            {/* --- My Portfolio Section --- */}
            <div className="portfolio-section" style={{ marginTop: 0 }}>
              <h3 style={{ color: isDarkMode ? "#fff" : "#333", fontSize: "1.15em", marginBottom: 12 }}>My Portfolio</h3>
              {portfolio.length === 0 ? (
                <div style={{ color: isDarkMode ? "#aaa" : "#666", fontSize: "1em" }}>No holdings.</div>
              ) : (
              <table className="table portfolio-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Quantity</th>
                    <th>Avg. Price</th>
                    <th>Total Cost</th>                
                  </tr>
                </thead>
                <tbody>
                  {portfolio.map((item, idx) => (
                    <tr key={idx}>
                      <td>
                        <Link to={`/exchange/${item.symbol || item.security_id}`} style={{ color: '#007bff', textDecoration: 'none', cursor: 'pointer' }}>
                          {item.symbol || item.security_id}
                        </Link>
                      </td>
                      <td>{item.quantity}</td>
                      <td>${formatNumber(item.avg_price)}</td>
                      <td>${formatNumber(item.quantity * item.avg_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              )}
            </div>
            <div className="portfolio-section" style={{ marginTop: 32 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ color: isDarkMode ? "#fff" : "#333", fontSize: "1.15em", margin: 0 }}>My Orders</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: isDarkMode ? '#a5a5a5' : '#666', fontSize: '0.9em' }}>Open</span>
                  <label style={{ 
                    position: 'relative', 
                    display: 'inline-block', 
                    width: '44px', 
                    height: '24px',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={!showOpenOrders}
                      onChange={(e) => setShowOpenOrders(!e.target.checked)}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: showOpenOrders ? '#4CAF50' : '#f44336',
                      borderRadius: '24px',
                      transition: '0.3s',
                      cursor: 'pointer'
                    }}>
                      <span style={{
                        position: 'absolute',
                        content: '',
                        height: '18px',
                        width: '18px',
                        left: showOpenOrders ? '3px' : '23px',
                        bottom: '3px',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        transition: '0.3s'
                      }}></span>
                    </span>
                  </label>
                  <span style={{ color: isDarkMode ? '#a5a5a5' : '#666', fontSize: '0.9em' }}>Filled</span>
                </div>
              </div>
              {filteredOrders.length === 0 ? (
                <div style={{ color: isDarkMode ? "#aaa" : "#666", fontSize: "1em" }}>
                  No {showOpenOrders ? 'open' : 'filled'} orders.
                </div>
              ) : (
              <table className="table portfolio-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Type</th>
                    <th>Quantity</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Date</th>
                    {showOpenOrders && <th>Edit</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order, idx) => (
                    <tr key={idx}>
                      <td>
                        <Link to={`/exchange/${order.symbol || order.security_id}`} style={{ color: '#007bff', textDecoration: 'none', cursor: 'pointer' }}>
                          {order.symbol || order.security_id}
                        </Link>
                      </td>
                      <td>{order.order_type || order.type}</td>
                      <td>{order.quantity}</td>
                      <td>${formatNumber(order.price)}</td>
                      <td>{order.status}</td>
                      <td>{order.created_at ? new Date(order.created_at).toLocaleDateString('en-US', { year: '2-digit', month: 'numeric', day: 'numeric' }) : ''}</td>
                      {showOpenOrders && (
                        <td>
                          <button
                            onClick={() => handleDeleteOrder(order._id || order.id)}
                            disabled={deletingOrder === (typeof order._id === 'object' && order._id.toString ? order._id.toString() : (order._id || order.id))}
                            style={{
                              background: '#ff4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              fontSize: '0.8em',
                              cursor: deletingOrder === (typeof order._id === 'object' && order._id.toString ? order._id.toString() : (order._id || order.id)) ? 'not-allowed' : 'pointer',
                              opacity: deletingOrder === (typeof order._id === 'object' && order._id.toString ? order._id.toString() : (order._id || order.id)) ? 0.6 : 1,
                              transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                              const orderIdString = typeof order._id === 'object' && order._id.toString ? order._id.toString() : (order._id || order.id);
                              if (deletingOrder !== orderIdString) {
                                e.target.style.background = '#ff6666';
                              }
                            }}
                            onMouseOut={(e) => {
                              const orderIdString = typeof order._id === 'object' && order._id.toString ? order._id.toString() : (order._id || order.id);
                              if (deletingOrder !== orderIdString) {
                                e.target.style.background = '#ff4444';
                              }
                            }}
                          >
                            {deletingOrder === (typeof order._id === 'object' && order._id.toString ? order._id.toString() : (order._id || order.id)) ? '...' : '×'}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              )}
            </div>
            
            <div className="portfolio-section" style={{ marginTop: 32 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ color: isDarkMode ? "#fff" : "#333", fontSize: "1.15em", margin: 0 }}>My Trades</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: isDarkMode ? '#a5a5a5' : '#666', fontSize: '0.9em' }}>Buys</span>
                  <label style={{ 
                    position: 'relative', 
                    display: 'inline-block', 
                    width: '44px', 
                    height: '24px',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={!showBuyTrades}
                      onChange={(e) => setShowBuyTrades(!e.target.checked)}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: showBuyTrades ? '#4CAF50' : '#f44336',
                      borderRadius: '24px',
                      transition: '0.3s',
                      cursor: 'pointer'
                    }}>
                      <span style={{
                        position: 'absolute',
                        content: '',
                        height: '18px',
                        width: '18px',
                        left: showBuyTrades ? '3px' : '23px',
                        bottom: '3px',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        transition: '0.3s'
                      }}></span>
                    </span>
                  </label>
                  <span style={{ color: isDarkMode ? '#a5a5a5' : '#666', fontSize: '0.9em' }}>Sells</span>
                </div>
              </div>
              {filteredTrades.length === 0 ? (
                <div style={{ color: isDarkMode ? "#aaa" : "#666", fontSize: "1em" }}>
                  No {showBuyTrades ? 'buy' : 'sell'} trades.
                </div>
              ) : (
              <table className="table portfolio-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Type</th>
                    <th>Quantity</th>
                    <th>Price</th>
                    <th>Total Value</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTrades.map((trade, idx) => (
                    <tr key={idx}>
                      <td>
                        <Link to={`/exchange/${trade.symbol || trade.security_id}`} style={{ color: '#007bff', textDecoration: 'none', cursor: 'pointer' }}>
                          {trade.symbol || trade.security_id}
                        </Link>
                      </td>
                      <td>{trade.trade_type || trade.type}</td>
                      <td>{trade.quantity}</td>
                      <td>${formatNumber(trade.price)}</td>
                      <td>${formatNumber(trade.quantity * trade.price)}</td>
                      <td>{trade.created_at ? new Date(trade.created_at).toLocaleDateString('en-US', { year: '2-digit', month: 'numeric', day: 'numeric' }) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              )}
            </div>
          </div>
        </div>
        
        {/* Deposit Modal */}
        {showDepositModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001
          }}>
            <div style={{
              background: isDarkMode ? '#2a2a2a' : 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}>
              <h3 style={{ 
                color: isDarkMode ? '#fff' : '#333', 
                marginBottom: '16px',
                fontSize: '1.2em'
              }}>
                Deposit Funds
              </h3>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  color: isDarkMode ? '#ccc' : '#666', 
                  fontSize: '0.9em',
                  display: 'block',
                  marginBottom: '8px'
                }}>
                  Deposit Amount (USD)
                </label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={depositAmount}
                  onChange={(e) => {
                    setDepositAmount(e.target.value);
                    setDepositError('');
                  }}
                  placeholder="Enter amount"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                    borderRadius: '6px',
                    background: isDarkMode ? '#1f1f1f' : '#f9f9f9',
                    color: isDarkMode ? '#fff' : '#333',
                    fontSize: '1em',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {depositError && (
                <div style={{
                  color: '#ff4444',
                  fontSize: '0.85em',
                  marginBottom: '16px',
                  padding: '8px',
                  background: 'rgba(255, 68, 68, 0.1)',
                  borderRadius: '4px'
                }}>
                  {depositError}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowDepositModal(false);
                    setDepositAmount('');
                    setDepositError('');
                  }}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                    color: isDarkMode ? '#ccc' : '#666',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: '0.9em'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!depositAmount || parseFloat(depositAmount) <= 0) {
                      setDepositError('Please enter a valid amount');
                      return;
                    }
                    if (Number(depositAmount) > 1000) {
                      setDepositError('Maximum deposit amount is $1,000');
                      return;
                    }

                    try {
                      const response = await fetch(`${API_BASE_URL}/api/user/${user.id}/deposit`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          amount: parseFloat(depositAmount),
                          transaction_id: 'TEST-' + Date.now(),
                          payment_method: 'test',
                          payer_email: profile?.email || 'test@test.com'
                        })
                      });

                      const result = await response.json();
                      
                      if (result.success) {
                        showMessage('Deposit successful! Your account has been credited.', 'success');
                        setShowDepositModal(false);
                        setDepositAmount('');
                        
                      setTimeout(() => {
                        window.location.reload();
                      }, 1500);
                      } else {
                        setDepositError(result.error || 'Deposit failed. Please try again.');
                      }
                    } catch (error) {
                      console.error('Error processing deposit:', error);
                      setDepositError('Error: ' + error.message);
                    }
                  }}
                  style={{
                    background: isDarkMode ? '#2c8640' : '#27bd27',
                    border: 'none',
                    color: 'white',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: '0.9em',
                    fontWeight: '500',
                    transition: 'background-color 0.2s ease'
                  }}
                    onMouseOver={(e) => e.target.style.background = isDarkMode ? '#3da855' : '#2ecc40'}
                    onMouseOut={(e) => e.target.style.background = isDarkMode ? '#2c8640' : '#27bd27'}
                >
                  Deposit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: isDarkMode ? '#2a2a2a' : 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}>
              <h3 style={{ 
                color: isDarkMode ? '#fff' : '#333', 
                marginBottom: '16px',
                fontSize: '1.2em'
              }}>
                Confirm Deletion
              </h3>
              <p style={{ 
                color: isDarkMode ? '#ccc' : '#666', 
                marginBottom: '24px',
                lineHeight: '1.5'
              }}>
                Are you sure you want to delete this order? This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setOrderToDelete(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                    color: isDarkMode ? '#ccc' : '#666',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: '0.9em'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteOrder}
                  style={{
                    background: '#ff4444',
                    border: 'none',
                    color: 'white',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: '0.9em'
                  }}
                >
                  Delete Order
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Message Modal */}
        {showMessageModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: isDarkMode ? '#2a2a2a' : 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '16px' 
              }}>
                <span style={{ 
                  fontSize: '1.5em', 
                  marginRight: '12px' 
                }}>
                  {modalType === 'success' ? '✅' : '❌'}
                </span>
                <h3 style={{ 
                  color: isDarkMode ? '#fff' : '#333',
                  margin: 0,
                  fontSize: '1.2em'
                }}>
                  {modalType === 'success' ? 'Success' : 'Error'}
                </h3>
              </div>
              <p style={{ 
                color: isDarkMode ? '#ccc' : '#666', 
                marginBottom: '24px',
                lineHeight: '1.5'
              }}>
                {modalMessage}
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowMessageModal(false)}
                  style={{
                    background: modalType === 'success' ? '#4CAF50' : '#ff4444',
                    border: 'none',
                    color: 'white',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: '0.9em'
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}